import type { SubAccount } from '@interfaces/goalSubAccountTypes';
import { AccountTypes } from '@onevesthq/ov-enums';
import {
  ACCOUNTS_FOR_TRANSFER_FROM_FHSA,
  ACCOUNTS_FOR_TRANSFER_TO_FHSA,
  INCOME_FUND_ACCOUNTS,
  LOCKED_IN_ACCOUNTS,
} from '@ovComponents/4-module/workflowCompletion/constants/accounts';
import { DEPOSIT_TRANSFER_TYPE } from '@ovComponents/4-module/workflowCompletion/constants/deposits';
import type { TransferContextData } from '@ovComponents/4-module/workflowCompletion/workflowTypes';

type TransferAccountsData = Pick<
  TransferContextData,
  'type' | 'subAccount' | 'fromSubAccount'
>;

type DepositErrorBannerKey =
  | 'transfer:depositEftLockedInAccountError'
  | 'transfer:depositEftIncomeFundsAccountError'
  | 'transfer:depositInternalTransferFromAccountError'
  | 'transfer:depositInternalTransferToAccountError'
  | 'transfer:depositFhsaInternalTransferAccountError';

const RESTRICTED_DEPOSIT_ACCOUNTS = [
  ...LOCKED_IN_ACCOUNTS,
  ...INCOME_FUND_ACCOUNTS,
];

export const isFhsaTransferRuleApplicable = ({
  applicableData,
}: {
  applicableData: TransferAccountsData;
}) => {
  const fromAccountType = applicableData.fromSubAccount?.account?.type;
  const toAccountType = applicableData.subAccount?.account?.type;
  const isFHSATransfer =
    fromAccountType === AccountTypes.FHSA ||
    toAccountType === AccountTypes.FHSA;

  const isLockedOrIncomeAccount = RESTRICTED_DEPOSIT_ACCOUNTS.some(
    (acc) => acc === fromAccountType || acc === toAccountType,
  );

  return (
    applicableData.type === DEPOSIT_TRANSFER_TYPE.INTERNAL_TRANSFER &&
    isFHSATransfer &&
    !isLockedOrIncomeAccount
  );
};

export const isFhsaTransferFormEnabled = ({
  applicableData,
  overrideTransferRestrictions,
}: {
  applicableData: TransferAccountsData;
  overrideTransferRestrictions: boolean;
}) =>
  overrideTransferRestrictions ||
  isValidFhsaTransfer(
    applicableData.fromSubAccount?.account?.type,
    applicableData.subAccount?.account?.type,
  );

export const isEftFormEnabled = ({
  selectedSubAccount,
  applicableData,
}: {
  selectedSubAccount: Pick<SubAccount, 'account'>;
  applicableData?: Pick<TransferContextData, 'type'> | null;
}) =>
  !RESTRICTED_DEPOSIT_ACCOUNTS.includes(selectedSubAccount.account.type) ||
  applicableData?.type !== DEPOSIT_TRANSFER_TYPE.EFT;

export const isInternalTransferFormEnabled = ({
  selectedSubAccount,
  counterpartSubAccount,
  applicableData,
}: {
  selectedSubAccount: Pick<SubAccount, 'account'>;
  counterpartSubAccount: Partial<SubAccount> | undefined;
  applicableData?: Pick<TransferContextData, 'type'> | null;
}) => {
  const counterpartAccountType = counterpartSubAccount?.account?.type;
  const isSelectedAccountAllowed =
    !RESTRICTED_DEPOSIT_ACCOUNTS.includes(selectedSubAccount.account.type) ||
    applicableData?.type !== DEPOSIT_TRANSFER_TYPE.INTERNAL_TRANSFER;
  const isCounterpartAccountAllowed =
    !counterpartAccountType ||
    !RESTRICTED_DEPOSIT_ACCOUNTS.includes(counterpartAccountType);

  return isSelectedAccountAllowed && isCounterpartAccountAllowed;
};

export const getDepositErrorBannerKey = ({
  applicableData,
  overrideTransferRestrictions,
}: {
  applicableData?: TransferAccountsData | null;
  overrideTransferRestrictions: boolean;
}): DepositErrorBannerKey | null => {
  const fromAccountType = applicableData?.fromSubAccount?.account?.type;
  const toAccountType = applicableData?.subAccount?.account?.type;

  if (
    toAccountType &&
    LOCKED_IN_ACCOUNTS.includes(toAccountType) &&
    applicableData?.type === DEPOSIT_TRANSFER_TYPE.EFT
  ) {
    return 'transfer:depositEftLockedInAccountError';
  }
  if (
    toAccountType &&
    INCOME_FUND_ACCOUNTS.includes(toAccountType) &&
    applicableData?.type === DEPOSIT_TRANSFER_TYPE.EFT
  ) {
    return 'transfer:depositEftIncomeFundsAccountError';
  }
  if (
    fromAccountType &&
    RESTRICTED_DEPOSIT_ACCOUNTS.includes(fromAccountType) &&
    applicableData?.type === DEPOSIT_TRANSFER_TYPE.INTERNAL_TRANSFER
  ) {
    return 'transfer:depositInternalTransferFromAccountError';
  }
  if (
    toAccountType &&
    RESTRICTED_DEPOSIT_ACCOUNTS.includes(toAccountType) &&
    applicableData?.type === DEPOSIT_TRANSFER_TYPE.INTERNAL_TRANSFER
  ) {
    return 'transfer:depositInternalTransferToAccountError';
  }

  if (
    !overrideTransferRestrictions &&
    !isValidFhsaTransfer(fromAccountType, toAccountType) &&
    applicableData?.type === DEPOSIT_TRANSFER_TYPE.INTERNAL_TRANSFER
  ) {
    return 'transfer:depositFhsaInternalTransferAccountError';
  }

  return null;
};

const isValidFhsaTransfer = (
  fromAccountType?: AccountTypes,
  toAccountType?: AccountTypes,
) => {
  const isValidTransferFromFhsa =
    fromAccountType !== AccountTypes.FHSA ||
    !toAccountType ||
    ACCOUNTS_FOR_TRANSFER_FROM_FHSA.includes(toAccountType);
  const isValidTransferToFhsa =
    toAccountType !== AccountTypes.FHSA ||
    !fromAccountType ||
    ACCOUNTS_FOR_TRANSFER_TO_FHSA.includes(fromAccountType);
  return isValidTransferFromFhsa && isValidTransferToFhsa;
};
